/**
 * UI Component Variant Definitions
 * @module shared/constants/ui-variants
 *
 * This file contains CVA (class-variance-authority) variant definitions
 * and icon mappings for UI components. These are separated from component
 * files to comply with React Fast Refresh requirements.
 *
 * Fast Refresh only works when a file exports components exclusively.
 * By moving variant functions and constants here, we maintain Fast Refresh
 * functionality while keeping type-safe variant management.
 *
 * Components are organized alphabetically to match the UI directory structure.
 */

import { cva } from 'class-variance-authority';
import { AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import {
  AccordionSize,
  AccordionVariant,
} from '@/shared/types/accordion.types';
import { AlertVariant } from '@/shared/types/alert.types';
import { AvatarSize } from '@/shared/types/avatar.types';
import { BadgeVariant } from '@/shared/types/badge.types';
import { BrandLogoSize } from '@/shared/types/brandlogo.types';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';
import {
  CardPadding,
  CardElevation,
  CardVariant,
} from '@/shared/types/card.types';
import { CheckboxSize, CheckboxVariant } from '@/shared/types/checkbox.types';
import {
  DropdownMenuSize,
  DropdownMenuItemVariant,
} from '@/shared/types/dropdown.types';
import { InputSize, InputVariant } from '@/shared/types/input.types';
import { ModalSize } from '@/shared/types/modal.types';
import {
  PaginationSize,
  PaginationAlignment,
} from '@/shared/types/pagination.types';
import { ProgressSize, ProgressVariant } from '@/shared/types/progress.types';
import {
  RadioGroupSize,
  RadioGroupOrientation,
} from '@/shared/types/radio.types';
import { SelectSize, SelectVariant } from '@/shared/types/select.types';
import {
  SeparatorThickness,
  SeparatorSpacing,
  SeparatorStyle,
} from '@/shared/types/separator.types';
import { SheetSide, SheetSize } from '@/shared/types/sheet.types';
import { SkeletonSpeed, SkeletonShape } from '@/shared/types/skeleton.types';
import { SliderSize, SliderVariant } from '@/shared/types/slider.types';
import { SwitchSize, SwitchVariant } from '@/shared/types/switch.types';
import { TableDensity } from '@/shared/types/table.types';
import {
  TabsSize,
  TabsVariant,
  TabsOrientation,
} from '@/shared/types/tabs.types';
import {
  TextareaSize,
  TextareaVariant,
  TextareaResize,
} from '@/shared/types/textarea.types';
import { ToastVariant } from '@/shared/types/toast.types';

// ============================================================================
// ACCORDION
// ============================================================================

/**
 * Accordion component variants
 */
export const accordionItemVariants = cva('border-b border-border', {
  variants: {
    variant: {
      [AccordionVariant.Default]: '',
      [AccordionVariant.Bordered]: 'mb-2 rounded-lg border px-4',
    },
  },
  defaultVariants: {
    variant: AccordionVariant.Default,
  },
});

export const accordionTriggerVariants = cva(
  'flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline active:scale-[0.98] [&[data-state=open]>svg]:rotate-180',
  {
    variants: {
      size: {
        [AccordionSize.Compact]: 'py-2 text-sm',
        [AccordionSize.Default]: 'py-4 text-base',
      },
    },
    defaultVariants: {
      size: AccordionSize.Default,
    },
  }
);

export const accordionContentVariants = cva(
  'overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
  {
    variants: {
      size: {
        [AccordionSize.Compact]: 'pb-2 pt-0',
        [AccordionSize.Default]: 'pb-4 pt-0',
      },
    },
    defaultVariants: {
      size: AccordionSize.Default,
    },
  }
);

// ============================================================================
// ALERT
// ============================================================================

/**
 * Alert component variants
 * Uses theme-aware CSS variables for consistent styling across all themes
 */
export const alertVariants = cva(
  'relative flex w-full gap-3 rounded-lg border-2 p-4 [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        [AlertVariant.Default]: 'border-border bg-muted text-foreground',
        [AlertVariant.Destructive]:
          'border-destructive/50 bg-destructive/10 text-destructive [&>svg]:text-destructive',
        [AlertVariant.Warning]:
          'border-warning-border bg-warning-muted text-warning [&>svg]:text-warning',
        [AlertVariant.Success]:
          'border-success-border bg-success-muted text-success [&>svg]:text-success',
        [AlertVariant.Info]:
          'border-info-border bg-info-muted text-info [&>svg]:text-info',
      },
      align: {
        start: 'justify-start text-left',
        center: 'justify-center text-center',
        end: 'justify-end text-right',
      },
      valign: {
        start: 'items-start [&>svg]:mt-0.5',
        center: 'items-center',
        end: 'items-end [&>svg]:mb-0.5',
      },
    },
    defaultVariants: {
      variant: AlertVariant.Default,
      align: 'start',
      valign: 'start',
    },
  }
);

/**
 * Alert variant to icon mapping
 * Maps each AlertVariant to its corresponding Lucide icon component
 */
export const ALERT_VARIANT_ICONS = {
  [AlertVariant.Default]: AlertCircle,
  [AlertVariant.Destructive]: AlertCircle,
  [AlertVariant.Warning]: AlertTriangle,
  [AlertVariant.Success]: CheckCircle,
  [AlertVariant.Info]: Info,
} as const;

// ============================================================================
// AVATAR
// ============================================================================

/**
 * Avatar component size variants
 * Provides standardized avatar sizes throughout the application
 */
export const avatarVariants = cva(
  'relative flex shrink-0 overflow-hidden rounded-full',
  {
    variants: {
      size: {
        [AvatarSize.Small]: 'size-8 text-xs',
        [AvatarSize.Medium]: 'size-10 text-sm',
        [AvatarSize.Large]: 'size-16 text-lg',
      },
    },
    defaultVariants: {
      size: AvatarSize.Medium,
    },
  }
);

// ============================================================================
// BADGE
// ============================================================================

/**
 * Badge component variants
 * Uses theme-aware CSS variables for consistent styling across all themes
 */
export const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        [BadgeVariant.Default]:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        [BadgeVariant.Secondary]:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        [BadgeVariant.Destructive]:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        [BadgeVariant.Outline]: 'border-border text-foreground',
        [BadgeVariant.Success]:
          'border-transparent bg-success text-success-foreground hover:bg-success/80',
        [BadgeVariant.Warning]:
          'border-transparent bg-warning text-warning-foreground hover:bg-warning/80',
        [BadgeVariant.Info]:
          'border-transparent bg-info text-info-foreground hover:bg-info/80',
      },
    },
    defaultVariants: {
      variant: BadgeVariant.Default,
    },
  }
);

// ============================================================================
// BRAND-LOGO
// ============================================================================

/**
 * Brand logo size variants
 */
export const brandLogoContainerVariants = cva(
  'flex items-center justify-center',
  {
    variants: {
      size: {
        [BrandLogoSize.Small]: 'size-8',
        [BrandLogoSize.Medium]: 'size-12',
        [BrandLogoSize.Large]: 'size-16',
      },
    },
    defaultVariants: {
      size: BrandLogoSize.Medium,
    },
  }
);

export const brandLogoIconVariants = cva('text-primary', {
  variants: {
    size: {
      [BrandLogoSize.Small]: 'size-8',
      [BrandLogoSize.Medium]: 'size-12',
      [BrandLogoSize.Large]: 'size-16',
    },
  },
  defaultVariants: {
    size: BrandLogoSize.Medium,
  },
});

export const brandLogoTextVariants = cva('font-semibold text-foreground', {
  variants: {
    size: {
      [BrandLogoSize.Small]: 'text-base',
      [BrandLogoSize.Medium]: 'text-lg',
      [BrandLogoSize.Large]: 'text-xl',
    },
  },
  defaultVariants: {
    size: BrandLogoSize.Medium,
  },
});

// ============================================================================
// BUTTON
// ============================================================================

/**
 * Button component variants
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        [ButtonVariant.Default]:
          'bg-primary text-primary-foreground hover:bg-primary/90',
        [ButtonVariant.Destructive]:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        [ButtonVariant.Outline]:
          'border border-input bg-background hover:bg-accent/30 hover:text-accent-foreground',
        [ButtonVariant.Secondary]:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        [ButtonVariant.Ghost]:
          'bg-transparent text-muted-foreground hover:bg-transparent hover:text-foreground focus:ring-0',
        [ButtonVariant.Link]: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        [ButtonSize.Default]: 'h-11 min-h-11 px-4 py-2',
        [ButtonSize.Small]: 'h-11 min-h-11 rounded-md px-3',
        [ButtonSize.Large]: 'h-11 min-h-11 rounded-md px-8',
        [ButtonSize.Icon]: 'size-11 min-h-11',
      },
    },
    defaultVariants: {
      variant: ButtonVariant.Default,
      size: ButtonSize.Default,
    },
  }
);

// ============================================================================
// CALENDAR
// ============================================================================

// Calendar component uses inline styling for complex date logic
// CVA variants not applicable for this component due to dynamic date calculations

// ============================================================================
// CARD
// ============================================================================

/**
 * Card component variants
 */
export const cardVariants = cva(
  'rounded-lg border border-border bg-card text-card-foreground transition-all',
  {
    variants: {
      variant: {
        [CardVariant.Default]: 'shadow-sm',
        [CardVariant.Clickable]:
          'cursor-pointer shadow-sm hover:border-ring hover:shadow-md active:scale-[0.98]',
        [CardVariant.Outlined]: 'border-2 shadow-none',
      },
      padding: {
        [CardPadding.None]: '',
        [CardPadding.Compact]: '[&>*]:p-4',
        [CardPadding.Default]: '[&>*]:p-6',
        [CardPadding.Spacious]: '[&>*]:p-8',
      },
      elevation: {
        [CardElevation.Flat]: 'shadow-none',
        [CardElevation.Low]: 'shadow-sm',
        [CardElevation.Medium]: 'shadow-md',
        [CardElevation.High]: 'shadow-lg',
      },
    },
    defaultVariants: {
      variant: CardVariant.Default,
      padding: CardPadding.Default,
      elevation: CardElevation.Low,
    },
  }
);

// ============================================================================
// CHECKBOX
// ============================================================================

/**
 * Checkbox component variants
 */
export const checkboxVariants = cva(
  'peer shrink-0 rounded-sm border ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-90 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:text-primary-foreground',
  {
    variants: {
      size: {
        [CheckboxSize.Small]: 'size-3.5',
        [CheckboxSize.Default]: 'size-4',
        [CheckboxSize.Large]: 'size-5',
      },
      variant: {
        [CheckboxVariant.Default]:
          'border-primary data-[state=checked]:bg-primary',
        [CheckboxVariant.Destructive]:
          'border-destructive data-[state=checked]:bg-destructive',
      },
    },
    defaultVariants: {
      size: CheckboxSize.Default,
      variant: CheckboxVariant.Default,
    },
  }
);

// ============================================================================
// DROPDOWN-MENU
// ============================================================================

/**
 * Dropdown menu item variants
 * Uses theme-aware CSS variables for consistent styling across all themes
 */
export const dropdownMenuItemVariants = cva(
  'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-all focus:bg-accent focus:text-accent-foreground active:scale-95 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  {
    variants: {
      variant: {
        [DropdownMenuItemVariant.Default]: '',
        [DropdownMenuItemVariant.Destructive]:
          'text-destructive focus:text-destructive',
        [DropdownMenuItemVariant.Success]: 'text-success focus:text-success',
      },
      size: {
        [DropdownMenuSize.Small]: 'min-h-11 px-2 py-1 text-xs',
        [DropdownMenuSize.Default]: 'min-h-11 px-2 py-1.5 text-sm',
      },
      inset: {
        true: 'pl-8',
        false: '',
      },
    },
    defaultVariants: {
      variant: DropdownMenuItemVariant.Default,
      size: DropdownMenuSize.Default,
      inset: false,
    },
  }
);

// ============================================================================
// FILTER-DROPDOWN
// ============================================================================

// Filter dropdown uses composition - no specific variants needed
// Styling controlled by parent DataTableWithFilters component

// ============================================================================
// INPUT
// ============================================================================

/**
 * Input component variants
 * Uses theme-aware CSS variables for consistent styling across all themes
 */
export const inputVariants = cva(
  'flex w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        [InputSize.Small]: 'h-9 min-h-11 px-2.5 py-1.5 text-xs',
        [InputSize.Default]: 'h-11 min-h-11 px-3 py-2 text-sm',
        [InputSize.Large]: 'h-12 min-h-11 px-4 py-3 text-base',
      },
      variant: {
        [InputVariant.Default]: 'border-input',
        [InputVariant.Error]:
          'border-destructive focus-visible:ring-destructive',
        [InputVariant.Success]: 'border-success focus-visible:ring-success',
      },
    },
    defaultVariants: {
      size: InputSize.Default,
      variant: InputVariant.Default,
    },
  }
);

// ============================================================================
// MODAL
// ============================================================================

/**
 * Modal content size variants
 */
export const modalContentVariants = cva(
  'fixed left-1/2 top-1/2 z-50 grid -translate-x-1/2 -translate-y-1/2 gap-4 border border-border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
  {
    variants: {
      size: {
        [ModalSize.Small]: 'w-full max-w-sm',
        [ModalSize.Default]: 'w-full max-w-lg',
        [ModalSize.Medium]: 'w-full max-w-2xl',
        [ModalSize.Large]: 'w-full max-w-4xl',
        [ModalSize.ExtraLarge]: 'w-full max-w-6xl',
        [ModalSize.Full]: 'h-[calc(100vh-2rem)] w-[calc(100vw-2rem)]',
      },
    },
    defaultVariants: {
      size: ModalSize.Default,
    },
  }
);

// ============================================================================
// PAGINATION
// ============================================================================

/**
 * Pagination container variants
 */
export const paginationVariants = cva('flex items-center gap-2', {
  variants: {
    size: {
      [PaginationSize.Compact]: 'gap-1 text-xs',
      [PaginationSize.Default]: 'gap-2 text-sm',
    },
    alignment: {
      [PaginationAlignment.Left]: 'justify-start',
      [PaginationAlignment.Center]: 'justify-center',
      [PaginationAlignment.Right]: 'justify-end',
    },
  },
  defaultVariants: {
    size: PaginationSize.Default,
    alignment: PaginationAlignment.Center,
  },
});

// ============================================================================
// PROGRESS
// ============================================================================

/**
 * Progress component variants
 */
export const progressVariants = cva(
  'relative w-full overflow-hidden rounded-full bg-secondary',
  {
    variants: {
      size: {
        [ProgressSize.Small]: 'h-2',
        [ProgressSize.Default]: 'h-4',
        [ProgressSize.Large]: 'h-6',
      },
    },
    defaultVariants: {
      size: ProgressSize.Default,
    },
  }
);

export const progressIndicatorVariants = cva(
  'size-full flex-1 transition-all',
  {
    variants: {
      variant: {
        [ProgressVariant.Default]: 'bg-primary',
        [ProgressVariant.Success]: 'bg-success',
        [ProgressVariant.Warning]: 'bg-warning',
        [ProgressVariant.Error]: 'bg-destructive',
      },
    },
    defaultVariants: {
      variant: ProgressVariant.Default,
    },
  }
);

// ============================================================================
// RADIO-GROUP
// ============================================================================

/**
 * Radio group variants
 */
export const radioGroupVariants = cva('grid gap-2', {
  variants: {
    orientation: {
      [RadioGroupOrientation.Horizontal]: 'grid-flow-col',
      [RadioGroupOrientation.Vertical]: 'grid-flow-row',
    },
  },
  defaultVariants: {
    orientation: RadioGroupOrientation.Vertical,
  },
});

export const radioGroupItemVariants = cva(
  'aspect-square rounded-full border border-primary text-primary ring-offset-background transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-90 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        [RadioGroupSize.Small]: 'size-3.5',
        [RadioGroupSize.Default]: 'size-4',
        [RadioGroupSize.Large]: 'size-5',
      },
    },
    defaultVariants: {
      size: RadioGroupSize.Default,
    },
  }
);

// ============================================================================
// SEARCH-INPUT
// ============================================================================

/**
 * Search input variants (reuses input variants)
 */
export const searchInputVariants = inputVariants;

// ============================================================================
// SELECT
// ============================================================================

/**
 * Select component variants
 * Uses theme-aware CSS variables for consistent styling across all themes
 */
export const selectTriggerVariants = cva(
  'flex w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        [SelectSize.Small]: 'h-9 px-2.5 py-1.5 text-xs',
        [SelectSize.Default]: 'h-11 px-3 py-2 text-sm',
        [SelectSize.Large]: 'h-12 px-4 py-3 text-base',
      },
      variant: {
        [SelectVariant.Default]: 'border-input',
        [SelectVariant.Error]: 'border-destructive focus:ring-destructive',
        [SelectVariant.Success]: 'border-success focus:ring-success',
      },
    },
    defaultVariants: {
      size: SelectSize.Default,
      variant: SelectVariant.Default,
    },
  }
);

// ============================================================================
// SEPARATOR
// ============================================================================

/**
 * Separator component variants
 */
export const separatorVariants = cva('shrink-0 bg-border', {
  variants: {
    orientation: {
      horizontal: 'w-full',
      vertical: 'h-full',
    },
    thickness: {
      [SeparatorThickness.Thin]: '',
      [SeparatorThickness.Default]: '',
      [SeparatorThickness.Thick]: '',
    },
    spacing: {
      [SeparatorSpacing.Tight]: '',
      [SeparatorSpacing.Default]: '',
      [SeparatorSpacing.Loose]: '',
    },
    style: {
      [SeparatorStyle.Solid]: '',
      [SeparatorStyle.Dashed]: 'border-dashed',
      [SeparatorStyle.Dotted]: 'border-dotted',
    },
  },
  compoundVariants: [
    {
      orientation: 'horizontal',
      thickness: SeparatorThickness.Thin,
      className: 'h-px',
    },
    {
      orientation: 'horizontal',
      thickness: SeparatorThickness.Default,
      className: 'h-px',
    },
    {
      orientation: 'horizontal',
      thickness: SeparatorThickness.Thick,
      className: 'h-0.5',
    },
    {
      orientation: 'vertical',
      thickness: SeparatorThickness.Thin,
      className: 'w-px',
    },
    {
      orientation: 'vertical',
      thickness: SeparatorThickness.Default,
      className: 'w-px',
    },
    {
      orientation: 'vertical',
      thickness: SeparatorThickness.Thick,
      className: 'w-0.5',
    },
    {
      orientation: 'horizontal',
      spacing: SeparatorSpacing.Tight,
      className: 'my-2',
    },
    {
      orientation: 'horizontal',
      spacing: SeparatorSpacing.Default,
      className: 'my-4',
    },
    {
      orientation: 'horizontal',
      spacing: SeparatorSpacing.Loose,
      className: 'my-6',
    },
    {
      orientation: 'vertical',
      spacing: SeparatorSpacing.Tight,
      className: 'mx-2',
    },
    {
      orientation: 'vertical',
      spacing: SeparatorSpacing.Default,
      className: 'mx-4',
    },
    {
      orientation: 'vertical',
      spacing: SeparatorSpacing.Loose,
      className: 'mx-6',
    },
  ],
  defaultVariants: {
    orientation: 'horizontal',
    thickness: SeparatorThickness.Default,
    spacing: SeparatorSpacing.Default,
    style: SeparatorStyle.Solid,
  },
});

// ============================================================================
// SHEET
// ============================================================================

/**
 * Sheet content variants with side-based positioning
 */
export const sheetContentVariants = cva(
  'fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out',
  {
    variants: {
      side: {
        [SheetSide.Left]:
          'inset-y-0 left-0 h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
        [SheetSide.Right]:
          'inset-y-0 right-0 h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
        [SheetSide.Top]:
          'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        [SheetSide.Bottom]:
          'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
      },
      size: {
        [SheetSize.Small]: '',
        [SheetSize.Default]: '',
        [SheetSize.Medium]: '',
        [SheetSize.Large]: '',
        [SheetSize.ExtraLarge]: '',
        [SheetSize.Full]: '',
      },
    },
    compoundVariants: [
      // Left/Right side sizes
      {
        side: [SheetSide.Left, SheetSide.Right],
        size: SheetSize.Small,
        className: 'w-3/4 sm:max-w-xs',
      },
      {
        side: [SheetSide.Left, SheetSide.Right],
        size: SheetSize.Default,
        className: 'w-3/4 sm:max-w-sm',
      },
      {
        side: [SheetSide.Left, SheetSide.Right],
        size: SheetSize.Medium,
        className: 'w-3/4 sm:max-w-md',
      },
      {
        side: [SheetSide.Left, SheetSide.Right],
        size: SheetSize.Large,
        className: 'w-3/4 sm:max-w-lg',
      },
      {
        side: [SheetSide.Left, SheetSide.Right],
        size: SheetSize.ExtraLarge,
        className: 'w-3/4 sm:max-w-2xl',
      },
      {
        side: [SheetSide.Left, SheetSide.Right],
        size: SheetSize.Full,
        className: 'w-full',
      },
      // Top/Bottom side sizes
      {
        side: [SheetSide.Top, SheetSide.Bottom],
        size: SheetSize.Small,
        className: 'h-1/4',
      },
      {
        side: [SheetSide.Top, SheetSide.Bottom],
        size: SheetSize.Default,
        className: 'h-1/3',
      },
      {
        side: [SheetSide.Top, SheetSide.Bottom],
        size: SheetSize.Medium,
        className: 'h-1/2',
      },
      {
        side: [SheetSide.Top, SheetSide.Bottom],
        size: SheetSize.Large,
        className: 'h-2/3',
      },
      {
        side: [SheetSide.Top, SheetSide.Bottom],
        size: SheetSize.ExtraLarge,
        className: 'h-3/4',
      },
      {
        side: [SheetSide.Top, SheetSide.Bottom],
        size: SheetSize.Full,
        className: 'h-full',
      },
    ],
    defaultVariants: {
      side: SheetSide.Left,
      size: SheetSize.Default,
    },
  }
);

// ============================================================================
// SKELETON
// ============================================================================

/**
 * Skeleton component variants
 */
export const skeletonVariants = cva(
  'relative overflow-hidden rounded-md bg-muted before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent',
  {
    variants: {
      speed: {
        [SkeletonSpeed.Slow]:
          'animate-pulse before:animate-[shimmer_3s_infinite]',
        [SkeletonSpeed.Normal]:
          'animate-pulse before:animate-[shimmer_2s_infinite]',
        [SkeletonSpeed.Fast]:
          'animate-pulse before:animate-[shimmer_1s_infinite]',
      },
      shape: {
        [SkeletonShape.Rectangle]: '',
        [SkeletonShape.Circle]: 'rounded-full',
        [SkeletonShape.Text]: 'h-4 w-full',
      },
    },
    defaultVariants: {
      speed: SkeletonSpeed.Normal,
      shape: SkeletonShape.Rectangle,
    },
  }
);

// ============================================================================
// SLIDER
// ============================================================================

/**
 * Slider component variants
 */
export const sliderTrackVariants = cva(
  'relative w-full grow overflow-hidden rounded-full bg-secondary',
  {
    variants: {
      size: {
        [SliderSize.Small]: 'h-1',
        [SliderSize.Default]: 'h-2',
        [SliderSize.Large]: 'h-3',
      },
    },
    defaultVariants: {
      size: SliderSize.Default,
    },
  }
);

export const sliderRangeVariants = cva('absolute h-full', {
  variants: {
    variant: {
      [SliderVariant.Default]: 'bg-primary',
      [SliderVariant.Success]: 'bg-success',
      [SliderVariant.Warning]: 'bg-warning',
    },
  },
  defaultVariants: {
    variant: SliderVariant.Default,
  },
});

export const sliderThumbVariants = cva(
  'block rounded-full border-2 bg-background shadow-lg ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      size: {
        [SliderSize.Small]: 'size-4',
        [SliderSize.Default]: 'size-5',
        [SliderSize.Large]: 'size-6',
      },
      variant: {
        [SliderVariant.Default]: 'border-primary',
        [SliderVariant.Success]: 'border-success',
        [SliderVariant.Warning]: 'border-warning',
      },
    },
    defaultVariants: {
      size: SliderSize.Default,
      variant: SliderVariant.Default,
    },
  }
);

// ============================================================================
// SWITCH
// ============================================================================

/**
 * Switch component variants
 * Uses theme-aware CSS variables for consistent styling across all themes
 */
export const switchVariants = cva(
  'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 data-[state=unchecked]:bg-input',
  {
    variants: {
      size: {
        [SwitchSize.Small]: 'h-5 w-9',
        [SwitchSize.Default]: 'h-6 w-11',
        [SwitchSize.Large]: 'h-7 w-14',
      },
      variant: {
        [SwitchVariant.Default]: 'data-[state=checked]:bg-primary',
        [SwitchVariant.Success]: 'data-[state=checked]:bg-success',
        [SwitchVariant.Destructive]: 'data-[state=checked]:bg-destructive',
      },
    },
    defaultVariants: {
      size: SwitchSize.Default,
      variant: SwitchVariant.Default,
    },
  }
);

export const switchThumbVariants = cva(
  'pointer-events-none block rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=unchecked]:translate-x-0',
  {
    variants: {
      size: {
        [SwitchSize.Small]: 'size-4 data-[state=checked]:translate-x-4',
        [SwitchSize.Default]: 'size-5 data-[state=checked]:translate-x-5',
        [SwitchSize.Large]: 'size-6 data-[state=checked]:translate-x-7',
      },
    },
    defaultVariants: {
      size: SwitchSize.Default,
    },
  }
);

// ============================================================================
// TABLE
// ============================================================================

/**
 * Table row variants
 */
export const tableRowVariants = cva(
  'border-b border-border transition-colors',
  {
    variants: {
      density: {
        [TableDensity.Compact]: '[&>td]:py-2 [&>th]:py-2',
        [TableDensity.Default]: '[&>td]:py-3 [&>th]:py-3',
        [TableDensity.Comfortable]: '[&>td]:py-4 [&>th]:py-4',
      },
      hoverable: {
        true: 'hover:bg-muted/50',
        false: '',
      },
      clickable: {
        true: 'cursor-pointer',
        false: '',
      },
      striped: {
        true: 'even:bg-muted/30',
        false: '',
      },
    },
    defaultVariants: {
      density: TableDensity.Default,
      hoverable: true,
      clickable: false,
      striped: false,
    },
  }
);

// ============================================================================
// TABS
// ============================================================================

/**
 * Tabs component variants
 */
export const tabsListVariants = cva(
  'inline-flex items-center p-1 text-muted-foreground',
  {
    variants: {
      variant: {
        [TabsVariant.Default]: 'rounded-md bg-muted',
        [TabsVariant.Pills]: 'rounded-full bg-muted',
        [TabsVariant.Underline]: 'border-b border-border bg-transparent p-0',
      },
      size: {
        [TabsSize.Small]: 'h-8 text-xs',
        [TabsSize.Default]: 'h-10 text-sm',
        [TabsSize.Large]: 'h-12 text-base',
      },
      orientation: {
        [TabsOrientation.Horizontal]: 'scrollbar-thin flex-row overflow-x-auto',
        [TabsOrientation.Vertical]: 'flex-col',
      },
    },
    defaultVariants: {
      variant: TabsVariant.Default,
      size: TabsSize.Default,
      orientation: TabsOrientation.Horizontal,
    },
  }
);

export const tabsTriggerVariants = cva(
  'inline-flex shrink-0 items-center justify-center whitespace-nowrap px-3 py-1.5 font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-95 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        [TabsVariant.Default]:
          'rounded-sm data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
        [TabsVariant.Pills]:
          'rounded-full data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
        [TabsVariant.Underline]:
          'rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground',
      },
      size: {
        [TabsSize.Small]: 'px-2 py-1 text-xs',
        [TabsSize.Default]: 'px-3 py-1.5 text-sm',
        [TabsSize.Large]: 'px-4 py-2 text-base',
      },
    },
    defaultVariants: {
      variant: TabsVariant.Default,
      size: TabsSize.Default,
    },
  }
);

// ============================================================================
// TEXTAREA
// ============================================================================

/**
 * Textarea component variants
 * Uses theme-aware CSS variables for consistent styling across all themes
 */
export const textareaVariants = cva(
  'flex w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        [TextareaSize.Small]: 'min-h-[60px] px-2.5 py-1.5 text-xs',
        [TextareaSize.Default]: 'min-h-[80px] px-3 py-2 text-sm',
        [TextareaSize.Large]: 'min-h-[120px] px-4 py-3 text-base',
      },
      variant: {
        [TextareaVariant.Default]: 'border-input',
        [TextareaVariant.Error]:
          'border-destructive focus-visible:ring-destructive',
        [TextareaVariant.Success]: 'border-success focus-visible:ring-success',
      },
      resize: {
        [TextareaResize.None]: 'resize-none',
        [TextareaResize.Vertical]: 'resize-y',
        [TextareaResize.Both]: 'resize',
      },
    },
    defaultVariants: {
      size: TextareaSize.Default,
      variant: TextareaVariant.Default,
      resize: TextareaResize.Vertical,
    },
  }
);

// ============================================================================
// TOAST
// ============================================================================

/**
 * Toast component variants
 * Uses theme-aware CSS variables for consistent styling across all themes
 */
export const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        [ToastVariant.Default]: 'border bg-background text-foreground',
        [ToastVariant.Destructive]:
          'destructive group border-destructive bg-destructive text-destructive-foreground',
        [ToastVariant.Warning]:
          'group border-warning-border bg-warning-muted text-warning',
        [ToastVariant.Success]:
          'group border-success-border bg-success-muted text-success',
        [ToastVariant.Info]: 'group border-info-border bg-info-muted text-info',
      },
    },
    defaultVariants: {
      variant: ToastVariant.Default,
    },
  }
);
